import { useEffect, useState, type CSSProperties } from 'react';
import { color, font, priorityTheme } from '../styles/theme';
import { dateTime, fullDate } from '../lib/format';
import { dueOn } from '../lib/deadlines';
import { mergeTimeline } from '../lib/ticketTimeline';
import type { TicketPriority, WorkFile } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { usePeople } from '../api/queries';
import {
  FILES_PER_TICKET,
  FILE_KINDS,
  PRIORITY_GUIDE,
  PRIORITY_LABEL,
  downloadTicketFile,
  useAddTicketComment,
  useAddTicketFile,
  useRemoveTicketFile,
  useUpdateTicket,
  useWorkTicket,
} from '../api/tickets';
import { Button, Card, ErrorNote, Field, Loading, inputStyle, selectStyle } from '../components/ui';
import { DueTag, PersonSelect } from './MeetingBits';
import { PriorityPill } from './TicketList';
import { AttachPicker, FileRow, StatusPill, ThreadItem, linkButton } from './TicketBits';

/**
 * One ticket as its own page. The conversation sits on the left — what was
 * asked, what has been said and attached since, and the composer — and the
 * facts the owner keeps sit on the right. Status changes live in the header's
 * action bar and nowhere else.
 *
 * The requester can reword their own and close or reopen it, and the person it
 * is assigned to can mark it done, drop it or reopen it; owner, priority and
 * due date need the update permission (the API's rule, PATCH /work/tickets/{id}).
 * The Owner picker lists every active account (GET /admins/people), so a role
 * that runs the queue without meetings:read still gets a person picker
 * (QA C-TIC-036). Anyone who can read adds an update, with files
 * or without; a file is removed by whoever attached it or by the queue. A
 * file goes up with the update it was picked with, so the thread shows it in
 * context, and the rail's index lists every file on the ticket.
 */

const col: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 18 };
const label: CSSProperties = { fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: color.muted, marginBottom: 4 };
const muted: CSSProperties = { fontSize: 12.5, color: color.muted, margin: 0 };
const small: CSSProperties = { padding: '5px 11px', fontSize: 12 };
const PRIORITIES: readonly TicketPriority[] = ['critical', 'normal', 'low'];

export function TicketPage({
  id,
  onBack,
  onOpenRedcap,
}: {
  id: string;
  onBack: () => void;
  /** For a ticket made from a REDCap flag: open that study's week. */
  onOpenRedcap?: (study: string) => void;
}) {
  const { admin, can } = useAuth();
  const canRunQueue = can('tickets:update');
  const detail = useWorkTicket(id);
  const people = usePeople(canRunQueue);
  const update = useUpdateTicket();
  const comment = useAddTicketComment();
  const addFile = useAddTicketFile();
  const removeFile = useRemoveTicketFile();

  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [note, setNote] = useState('');
  const [picked, setPicked] = useState<File[]>([]);
  const [progress, setProgress] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const t = detail.data?.ticket;
  const files = detail.data?.files ?? [];
  const thread = mergeTimeline(detail.data?.comments ?? [], files);
  const mine = !!t && !!admin && t.requesterId === admin.id;
  const assignedToMe = !!t && !!admin && t.ownerId === admin.id;
  const canClose = canRunQueue || mine || assignedToMe;
  const open = t?.status === 'open';
  const canEditFacts = canRunQueue && open;

  useEffect(() => {
    if (t && !editing) {
      setTitle(t.title);
      setDetails(t.details ?? '');
    }
  }, [t, editing]);

  const patch = (p: Parameters<typeof update.mutate>[0] extends infer P ? Omit<P & object, 'id'> : never) =>
    update.mutate({ id, ...(p as object) });

  async function saveEdit() {
    await update.mutateAsync({ id, title: title.trim(), details: details.trim() || null });
    setEditing(false);
  }

  /** The update first, then each file against it; files alone go up on their own. What fails stays picked. */
  async function say() {
    const body = note.trim();
    if (!body && picked.length === 0) return;
    setSendError(null);
    try {
      let commentId: string | null = null;
      if (body) {
        setProgress('Adding…');
        const res = await comment.mutateAsync({ id, body });
        commentId = res.comment.id;
        setNote('');
      }
      const queue = [...picked];
      for (let i = 0; i < queue.length; i++) {
        const file = queue[i]!;
        setProgress(queue.length > 1 ? `Uploading ${i + 1} of ${queue.length}…` : 'Uploading…');
        await addFile.mutateAsync({ id, file, commentId });
        setPicked((prev) => prev.filter((f) => f !== file));
      }
    } catch (e) {
      setSendError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setProgress(null);
    }
  }

  function download(f: WorkFile) {
    setFileError(null);
    downloadTicketFile(id, f).catch((e: unknown) => setFileError(e instanceof Error ? e.message : 'The download failed.'));
  }

  function remove(f: WorkFile) {
    setFileError(null);
    removeFile.mutateAsync({ id, fileId: f.id }).catch((e: unknown) => setFileError(e instanceof Error ? e.message : 'Something went wrong.'));
  }

  const back = (
    <button onClick={onBack} style={{ ...linkButton, fontSize: 13, alignSelf: 'flex-start' }}>
      ← All tickets
    </button>
  );

  if (detail.isLoading && !t) {
    return (
      <div className="fade-in" style={col}>
        {back}
        <Loading label="Opening the ticket…" />
      </div>
    );
  }
  if (detail.error || !t) {
    return (
      <div className="fade-in" style={col}>
        {back}
        <ErrorNote message={detail.error ? (detail.error as Error).message : 'No such ticket.'} />
      </div>
    );
  }

  const due = t.dueOn ? dueOn(t.dueOn) : null;
  const canSend = (note.trim().length > 0 || picked.length > 0) && !progress;

  return (
    <div className="fade-in" style={col}>
      {back}

      {/* Identity and the action bar. Status changes happen here and nowhere else. */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: color.blueDeep }}>{t.ref}</span>
          <PriorityPill priority={t.priority} />
          <StatusPill status={t.status} />
          <span style={{ flex: 1 }} />
          {t.source?.type === 'redcap_flag' && onOpenRedcap && (
            <Button
              variant="soft"
              style={small}
              onClick={() => onOpenRedcap(t.source!.key.split('|')[0]!)}
              title="This ticket was made from a flag on the REDCap weekly page"
            >
              Open in REDCap
            </Button>
          )}
          {canClose &&
            (open ? (
              <>
                <Button variant="navy" style={small} onClick={() => patch({ status: 'done' })} disabled={update.isPending}>
                  Mark done
                </Button>
                <Button variant="ghost" style={small} onClick={() => patch({ status: 'dropped' })} disabled={update.isPending}>
                  Drop
                </Button>
              </>
            ) : (
              <Button variant="ghost" style={small} onClick={() => patch({ status: 'open' })} disabled={update.isPending}>
                Reopen
              </Button>
            ))}
        </div>
        <h1 style={{ fontFamily: font.display, fontWeight: 600, fontSize: 22, color: color.navy, margin: '8px 0 0', overflowWrap: 'anywhere' }}>
          {t.title}
        </h1>
        <p style={{ fontSize: 13, color: color.muted, margin: '4px 0 0' }}>
          Filed by {t.requesterName ?? '—'} · {fullDate(t.createdAt)}
          {t.closedAt && (
            <>
              {' '}
              · Closed by {t.closedByName ?? 'someone'}, {dateTime(t.closedAt)}
            </>
          )}
        </p>
        {update.error && <ErrorNote message={(update.error as Error).message} />}
      </div>

      <div className="meeting-cols">
        {/* The conversation. */}
        <div style={col}>
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ ...label, marginBottom: 0, flex: 1 }}>Request</div>
              {(canRunQueue || mine) && !editing && (
                <Button variant="ghost" style={small} onClick={() => setEditing(true)}>
                  Edit
                </Button>
              )}
            </div>
            {editing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Field label="Title">
                  <input value={title} onChange={(e) => setTitle(e.target.value)} style={inputStyle} />
                </Field>
                <Field label="Details">
                  <textarea
                    value={details}
                    rows={5}
                    onChange={(e) => setDetails(e.target.value)}
                    style={{ ...inputStyle, fontSize: 14, lineHeight: '21px', resize: 'vertical' }}
                  />
                </Field>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="navy" onClick={() => void saveEdit()} disabled={!title.trim() || update.isPending}>
                    Save
                  </Button>
                  <Button variant="ghost" onClick={() => setEditing(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <p style={{ fontSize: 14, lineHeight: '21px', color: t.details ? color.ink : color.muted, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', margin: 0 }}>
                {t.details ?? 'No details given.'}
              </p>
            )}
          </Card>

          <Card>
            <div style={label}>Updates</div>
            {thread.length === 0 ? (
              <p style={muted}>Nothing said yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8 }}>
                {thread.map((item) => (
                  <ThreadItem key={`${item.kind}-${item.id}`} item={item} onDownload={download} />
                ))}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16, paddingTop: 16, borderTop: `1px solid ${color.line}` }}>
              <textarea
                value={note}
                rows={3}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    void say();
                  } else if (e.key === 'Escape' && note.trim()) {
                    e.stopPropagation();
                  }
                }}
                placeholder="Add an update — what happened, or what you need to know"
                aria-label="Add an update"
                disabled={!!progress}
                style={{ ...inputStyle, fontSize: 14, lineHeight: '20px', resize: 'vertical' }}
              />
              <AttachPicker files={picked} onChange={setPicked} have={files.length} disabled={!!progress} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Button variant="navy" onClick={() => void say()} disabled={!canSend} title="Ctrl/⌘-Enter adds it too">
                  {progress ?? 'Add update'}
                </Button>
                <span style={{ fontSize: 12, color: color.muted }}>
                  Up to 10 MB a file, {FILES_PER_TICKET} a ticket · {FILE_KINDS}
                </span>
              </div>
              {sendError && <ErrorNote message={sendError} />}
            </div>
          </Card>
        </div>

        {/* The facts the owner keeps, and the index of files. */}
        <div style={{ ...col, position: 'sticky', top: 16 }}>
          <Card>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div style={label}>Owner</div>
                {canEditFacts && people.data ? (
                  <PersonSelect
                    label="Owner"
                    value={t.ownerId}
                    currentName={t.ownerName}
                    people={people.data.people}
                    attendees={[]}
                    disabled={update.isPending}
                    onChange={(ownerId) => patch({ ownerId })}
                  />
                ) : (
                  <div style={{ fontSize: 13.5, color: t.ownerName ? color.ink : '#8a4f16', fontWeight: t.ownerName ? 400 : 800 }}>
                    {t.ownerName ?? (open ? 'Needs an owner' : '—')}
                  </div>
                )}
              </div>

              <div>
                <div style={label}>Due</div>
                {canEditFacts ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                    <input
                      type="date"
                      aria-label="Due date"
                      value={t.dueOn ?? ''}
                      disabled={update.isPending}
                      onChange={(e) => patch({ dueOn: e.target.value || null })}
                      style={{ ...inputStyle, fontSize: 12.5, padding: '5px 8px', width: 'auto' }}
                    />
                    {due && due.state !== 'upcoming' && <DueTag dueOn={t.dueOn} status={t.status} />}
                  </div>
                ) : (
                  <DueTag dueOn={t.dueOn} status={t.status} />
                )}
              </div>

              <div>
                <div style={label}>Priority</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span aria-hidden style={{ width: 10, height: 10, borderRadius: 999, background: priorityTheme[t.priority].fg, flex: 'none' }} />
                  {canEditFacts ? (
                    <select
                      aria-label="Priority"
                      value={t.priority}
                      disabled={update.isPending}
                      onChange={(e) => patch({ priority: e.target.value as TicketPriority })}
                      style={{ ...selectStyle, fontSize: 12.5, padding: '6px 28px 6px 10px', width: 'auto' }}
                    >
                      {PRIORITIES.map((p) => (
                        <option key={p} value={p}>
                          {PRIORITY_LABEL[p]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span style={{ fontSize: 13.5, color: color.ink }}>{PRIORITY_LABEL[t.priority]}</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: color.muted, marginTop: 4 }}>{PRIORITY_GUIDE[t.priority].line}</div>
              </div>

              {t.studySlug && (
                <div>
                  <div style={label}>Study</div>
                  <div style={{ fontSize: 13.5, color: color.ink }}>{t.studySlug}</div>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <div style={label}>
              Files
              {files.length > 0 && ` · ${files.length}`}
            </div>
            {files.length === 0 ? (
              <p style={muted}>No files yet. Attach one with an update.</p>
            ) : (
              <div style={{ marginTop: 4 }}>
                {files.map((f) => (
                  <FileRow
                    key={f.id}
                    file={f}
                    onDownload={() => download(f)}
                    onRemove={canRunQueue || (!!admin && f.uploaderId === admin.id) ? () => remove(f) : undefined}
                    busy={removeFile.isPending}
                  />
                ))}
              </div>
            )}
            {fileError && <ErrorNote message={fileError} />}
          </Card>
        </div>
      </div>
    </div>
  );
}
